import React, { useEffect, useState } from "react";
import { BrowserRouter, Switch } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { CircularProgress } from "@material-ui/core";

import LoggedInLayout from "../layout";
import ErrorBoundary from "../components/ErrorBoundary";
import Dashboard from "../pages/Dashboard/";
import TicketResponsiveContainer from "../pages/TicketResponsiveContainer";
import Login from "../pages/Login/";
import ForgotPassword from "../pages/ForgotPassword/";
import ResetPassword from "../pages/ResetPassword/";
import ResetPasswordLegacyRedirect from "../pages/ResetPassword/LegacyRedirect";
import NotFound from "../pages/NotFound/";
import SsoLanding from "../pages/SsoLanding/";
// import Companies from "../pages/Companies/";
import { AuthProvider } from "../context/Auth/AuthContext";
import { TicketsContextProvider } from "../context/Tickets/TicketsContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import Route from "./Route";

import Admin from "../pages/Admin";
import isNativeApp from "../utils/isNativeApp";

// Route-level code splitting: rarely-hit / heavy pages (AI, admin,
// campaigns, settings, reports) load on demand instead of bloating the
// entry bundle. Hot path stays eager: Login/Reset/SSO, Dashboard,
// TicketResponsiveContainer, NotFound, Admin.
const Connections = React.lazy(() => import("../pages/Connections/"));
const SettingsCustom = React.lazy(() => import("../pages/SettingsCustom/"));
const Users = React.lazy(() => import("../pages/Users"));
const Contacts = React.lazy(() => import("../pages/Contacts/"));
const Queues = React.lazy(() => import("../pages/Queues/"));
const Tags = React.lazy(() => import("../pages/Tags/"));
const MessagesAPI = React.lazy(() => import("../pages/MessagesAPI/"));
const QuickMessages = React.lazy(() => import("../pages/QuickMessages/"));
const Schedules = React.lazy(() => import("../pages/Schedules"));
const Files = React.lazy(() => import("../pages/Files"));
const Rankings = React.lazy(() => import("../pages/Rankings/"));
const WhatsAppBackups = React.lazy(() => import("../pages/WhatsAppBackups"));
const Annoucements = React.lazy(() => import("../pages/Annoucements"));
const Chat = React.lazy(() => import("../pages/Chat"));
const ToDoList = React.lazy(() => import("../pages/ToDoList/"));
const AIDashboard = React.lazy(() => import("../pages/AIDashboard"));
const AISettings = React.lazy(() => import("../pages/AISettings"));
const VoiceSettings = React.lazy(() => import("../pages/VoiceSettings"));
const VoiceIntelligence = React.lazy(() => import("../pages/VoiceIntelligence"));
const AIWorkforce = React.lazy(() => import("../pages/AIWorkforce"));
const AIConfigWizard = React.lazy(() => import("../pages/AIConfigWizard"));
const Teams = React.lazy(() => import("../pages/Teams"));
const Campaigns = React.lazy(() => import("../pages/Campaigns"));
const CampaignDetails = React.lazy(() => import("../pages/Campaigns/CampaignDetails"));
const Offers = React.lazy(() => import("../pages/Offers"));
const AIOS = React.lazy(() => import("../pages/AIOS"));
const MarketingWorkspace = React.lazy(() => import("../aios/growth/MarketingWorkspace"));
const Pipelines = React.lazy(() => import("../pages/Pipelines"));
const Billing = React.lazy(() => import("../pages/Billing"));
const BillingSuccess = React.lazy(() => import("../pages/BillingSuccess"));
const Appointments = React.lazy(() => import("../pages/Appointments"));

const Routes = () => {

  return (
    <BrowserRouter>
      <AuthProvider>
        <TicketsContextProvider>
          <Switch>
            <Route exact path="/login" component={Login} />
            <Route exact path="/forgot-password" component={ForgotPassword} />
            {/* Token now arrives in window.location.hash (#token=<hex>). The
                tokenless route is the canonical landing. The :token route
                stays as a client-side redirect for password-reset emails
                sent BEFORE this deploy — token TTL is 1h, so it can be
                removed ~1h after prod ship. Order matters: the more
                specific :token route MUST come first or the bare
                /reset-password will swallow it. */}
            <Route exact path="/reset-password/:token" component={ResetPasswordLegacyRedirect} />
            <Route exact path="/reset-password" component={ResetPassword} />
            <Route exact path="/auth/sso" component={SsoLanding} />
            {/* <Route exact path="/create-company" component={Companies} /> */}
            {/* Whapiz super-admin shell — mounted OUTSIDE LoggedInLayout
                so the GoDeal tenant sidebar/header don't wrap it. The
                Admin component owns its own branded chrome. Route stays
                isPrivate so unauthenticated users get bounced to /login;
                the super-only gate is enforced inside Admin (and by the
                backend isSuper middleware on every endpoint it calls). */}
            <Route exact path="/admin/companies/:id" component={Admin} isPrivate />
            <Route exact path="/admin/:tab?" component={Admin} isPrivate />
            <WhatsAppsProvider>
              <LoggedInLayout>
                {/* BugReporterWidget moved into the AppBar toolbar (layout). */}
                {/* App-wide crash protection: a render error in any page shows a
                    fallback instead of white-screening the whole app. */}
                <ErrorBoundary page>
                {/* Switch wrapper so the catch-all NotFound at the bottom
                    only renders when no other in-app route matches. Without
                    it the bare Route components above all render in parallel
                    by path-exact, which kept unknown URLs as a blank shell. */}
                <React.Suspense fallback={<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}><CircularProgress /></div>}>
<Switch>
                <Route exact path="/" component={Dashboard} isPrivate />
                <Route
                  exact
                  path="/tickets/:ticketId?"
                  component={TicketResponsiveContainer}
                  isPrivate
                />
                <Route
                  exact
                  path="/connections"
                  component={Connections}
                  isPrivate
                  adminOnly
                />
                <Route
                  exact
                  path="/quick-messages"
                  component={QuickMessages}
                  isPrivate
                />
                <Route
                  exact
                  path="/schedules"
                  component={Schedules}
                  isPrivate
                />
                <Route
                  exact
                  path="/todolist"
                  component={ToDoList}
                  isPrivate
                />
                <Route exact path="/tags" component={Tags} isPrivate />
                <Route exact path="/files" component={Files} isPrivate />
                <Route
                  exact
                  path="/whatsapp-backups"
                  component={WhatsAppBackups}
                  isPrivate
                  adminOnly
                />
                <Route exact path="/rankings" component={Rankings} isPrivate />
                <Route exact path="/contacts" component={Contacts} isPrivate />
                <Route exact path="/users" component={Users} isPrivate adminOnly />
                <Route
                  exact
                  path="/messages-api"
                  component={MessagesAPI}
                  isPrivate
                  adminOnly
                />
                <Route
                  exact
                  path="/settings"
                  component={SettingsCustom}
                  isPrivate
                  adminOnly
                />
                 
                <Route exact path="/queues" component={Queues} isPrivate adminOnly />
                <Route
                  exact
                  path="/announcements"
                  component={Annoucements}
                  isPrivate
                  adminOnly
                />
                 
                
                <Route exact path="/chats/:id?" component={Chat} isPrivate />
                <Route exact path="/ai-dashboard" component={AIDashboard} isPrivate adminOnly />
                {/* Apple App Store Guideline 2.1: hide AI Settings in native app —
                    the page calls /ai/company-settings and shows a toast error
                    when OpenAI is unconfigured (as in Apple's test account). */}
                {!isNativeApp() && (
                  <Route exact path="/ai-settings" component={AISettings} isPrivate adminOnly />
                )}
                <Route exact path="/voice-settings" component={VoiceSettings} isPrivate adminOnly />
                <Route exact path="/voice-intelligence" component={VoiceIntelligence} isPrivate />
                <Route exact path="/ai-workforce" component={AIWorkforce} isPrivate adminOnly />
                {/* Slice-8 Gate-1: AI-OS shell skeleton. Hidden in the native
                    app exactly like /ai-settings (Apple Guideline 2.1) — the
                    AI surfaces are unusable on Apple's test account and the
                    reviewer must land on NotFound instead. */}
                {!isNativeApp() && [
                  <Route key="ai-os" exact path="/ai-os" component={AIOS} isPrivate adminOnly />,
                  <Route key="ai-os-marketing" exact path="/ai-os/marketing" component={MarketingWorkspace} isPrivate adminOnly />,
                ]}
                <Route exact path="/ai-config-wizard" component={AIConfigWizard} isPrivate adminOnly />
                <Route exact path="/teams" component={Teams} isPrivate adminOnly />
                <Route exact path="/campaigns" component={Campaigns} isPrivate adminOnly />
                <Route exact path="/campaigns/:id" component={CampaignDetails} isPrivate adminOnly />
                <Route exact path="/offers" component={Offers} isPrivate adminOnly />
                <Route exact path="/pipelines" component={Pipelines} isPrivate adminOnly />
                {/* Apple App Store Guideline 3.1.1: don't register the /billing
                    route inside the native app. A reviewer hand-typing /billing
                    lands on NotFound instead of a page that shows prices. */}
                {!isNativeApp() && (
                  <>
                    <Route exact path="/billing/success" component={BillingSuccess} isPrivate adminOnly />
                    <Route exact path="/billing" component={Billing} isPrivate adminOnly />
                  </>
                )}
                <Route exact path="/appointments" component={Appointments} isPrivate />

                {/* Catch-all 404 — must stay LAST in the inner Switch.
                    isPrivate so unauthenticated visitors are bounced to
                    /login instead of seeing a branded 404 they can't act on. */}
                <Route component={NotFound} isPrivate />
                </Switch>
</React.Suspense>
                </ErrorBoundary>
              </LoggedInLayout>
            </WhatsAppsProvider>
          </Switch>
          {/*
            SKILL §8 toast-accessibility / toast-dismiss:
            - autoClose 4000ms (in 3-5s recommended range; 3000 was a
              little tight for non-trivial messages)
            - role=status + aria-live=polite so screen readers
              announce toasts without stealing focus
            - hideProgressBar=false: keeps the visual cue for how
              much time is left, helps with scannability
            - newestOnTop=true so important fresh messages aren't
              hidden behind a stale stack
          */}
          <ToastContainer
            autoClose={4000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            pauseOnFocusLoss
            draggable
            pauseOnHover
            role="status"
            aria-live="polite"
          />
        </TicketsContextProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
